import { useDisclosure } from '@chakra-ui/react'
import { useFlags } from '@missionlabs/feature-flags'
import {
    Body,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    useToast
} from '@missionlabs/react'
import { FC, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useGetNumberByID } from 'shared/hooks/useGetNumberByID'
import { useDeleteNumberMutation } from 'shared/store'

interface RemoveNumberDialogProps {}

export const RemoveNumberDialog: FC<RemoveNumberDialogProps> = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { toast } = useToast()

    const { t } = useTranslation()
    const { id: numberID = '' } = useParams()
    const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true })

    const flags = useFlags(['admin_numbers_add_remove', 'admin_numbers_port_mirroring'])

    const number = useGetNumberByID(numberID)
    const ref = useRef<HTMLButtonElement>(null)

    const [removeNumber, { isLoading: isRemoving }] = useDeleteNumberMutation()

    const canRemoveNumber =
        flags.admin_numbers_add_remove.enabled ||
        (flags.admin_numbers_port_mirroring.enabled && number?.displayNumberType === 'Mirrored')

    useEffect(() => {
        if (!number || !canRemoveNumber) onClose()
    }, [canRemoveNumber, number, onClose])

    const handleTransition = () => {
        const end = location.pathname.split('/').pop()
        if (end === 'remove') navigate('/admin/numbers')
    }

    const handleConfirm = async () => {
        try {
            if (!number) throw Error(`Can't remove number: ${number}`)
            await removeNumber(number.ID).unwrap()
        } catch (e) {
            const msg =
                e?.data?.statusCode === 400 &&
                e?.data?.message.toLowerCase().includes('cannot delete last')
                    ? 'numbers.lastNumberError'
                    : 'numbers.removeError'

            toast({
                title: t(msg),
                status: 'error'
            })
        } finally {
            onClose()
        }
    }

    if (!number) return null

    return (
        <Dialog
            leastDestructiveRef={ref}
            isOpen={isOpen}
            onClose={onClose}
            onCloseComplete={handleTransition}
        >
            <DialogOverlay />
            <DialogContent>
                <DialogCloseButton />
                <DialogHeader>
                    {t('numbers.removeDialog.heading', { number: number.numberE164 })}
                </DialogHeader>
                <DialogBody>
                    <Body>{t('numbers.removeDialog.body')}</Body>
                </DialogBody>
                <DialogFooter
                    cancel={{ ref, label: t('Cancel') }}
                    confirm={{
                        label: t('numbers.removeDialog.removeNumber'),
                        variant: 'negatory',
                        onClick: handleConfirm
                    }}
                    isLoading={isRemoving}
                />
            </DialogContent>
        </Dialog>
    )
}
